/**
 * Analytics Tracker
 *
 * Thin facade over an AnalyticsProvider. Exposes one typed method per GA4
 * ecommerce event so call-sites never write raw event name strings.
 *
 * Provider resolution is deferred to the first track call so GTM / GA4 have
 * time to initialise their globals (window.dataLayer / window.gtag) before
 * we check for them.
 */

/**
 * Internal dependencies
 */
import { Ga4Provider } from "./providers/ga4";
import { NoopProvider } from "./providers/noop";
import type { AnalyticsItem, AnalyticsProvider } from "./types";

class AnalyticsTracker {
	/**
	 * Resolve the provider fresh on every call.
	 *
	 * This allows GTM to finish loading its async snippet between mount and the
	 * first user interaction without locking us into a NoopProvider prematurely.
	 */
	private provider(): AnalyticsProvider {
		return resolveProvider();
	}

	/** GA4: view_item — fired when a product page loads. */
	viewItem(currency: string, items: AnalyticsItem[]): void {
		this.provider().track("view_item", { currency, items });
	}

	/** GA4: select_item — fired when the user picks a variant. */
	selectItem(currency: string, items: AnalyticsItem[]): void {
		this.provider().track("select_item", { currency, items });
	}

	/** GA4: add_to_cart — fired after a successful add-to-cart API call. */
	addToCart(currency: string, value: number, items: AnalyticsItem[]): void {
		this.provider().track("add_to_cart", { currency, value, items });
	}

	/** GA4: view_cart — fired when the cart page mounts with items. */
	viewCart(currency: string, value: number, items: AnalyticsItem[]): void {
		this.provider().track("view_cart", { currency, value, items });
	}

	/** GA4: remove_from_cart — fired when an item is removed from the cart. */
	removeFromCart(
		currency: string,
		value: number,
		items: AnalyticsItem[],
	): void {
		this.provider().track("remove_from_cart", { currency, value, items });
	}

	/** GA4: begin_checkout — fired when the user clicks "Checkout" from the cart. */
	beginCheckout(
		currency: string,
		value: number,
		items: AnalyticsItem[],
	): void {
		this.provider().track("begin_checkout", { currency, value, items });
	}

	/** GA4: add_shipping_info — fired after billing/delivery info is saved. */
	addShippingInfo(
		currency: string,
		value: number,
		items: AnalyticsItem[],
		shippingTier = "Standard",
	): void {
		this.provider().track("add_shipping_info", {
			currency,
			value,
			items,
			shipping_tier: shippingTier,
		});
	}

	/** GA4: add_payment_info — fired when the payment step is reached. */
	addPaymentInfo(
		currency: string,
		value: number,
		items: AnalyticsItem[],
		paymentType = "Card",
	): void {
		this.provider().track("add_payment_info", {
			currency,
			value,
			items,
			payment_type: paymentType,
		});
	}

	/** GA4: purchase — fired on the order confirmation page when the order loads. */
	purchase(
		transactionId: string,
		currency: string,
		value: number,
		items: AnalyticsItem[],
	): void {
		this.provider().track("purchase", {
			transaction_id: transactionId,
			currency,
			value,
			items,
		});
	}

	/** Custom: payment_error — fired when Stripe returns an error. */
	paymentError(message: string, paymentType = "card"): void {
		this.provider().track("payment_error", {
			payment_type: paymentType,
			error_message: message,
		});
	}
}

function resolveProvider(): AnalyticsProvider {
	if ( Array.isArray( window.dataLayer ) || typeof window.gtag === "function" ) {
		return new Ga4Provider();
	}
	return new NoopProvider();
}

export const tracker = new AnalyticsTracker();
